import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, CaretRight, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { useTransferPotMutation } from '../../features/pots/queries';
import {
  getPurposeMeta,
  resolvePotPurpose,
  resolvePotVaultIcon,
} from '../../lib/potPurposeUtils';
import { cn } from '../../utils/cn';
import { parsePtNumber } from '../../utils/money';
import { formatEUR } from '../../utils/money';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
} from '../common/BottomSheet';
import { Button } from '../ui/Button';

const QUICK_AMOUNTS = [50, 100, 200];

function PotPickerCard({ pot, selected, onSelect, balanceLabel }) {
  const purpose = resolvePotPurpose(pot);
  const meta = getPurposeMeta(purpose);
  const Icon = resolvePotVaultIcon(pot, purpose);

  return (
    <button
      type="button"
      onClick={() => onSelect(pot)}
      className={cn(
        'flex w-full items-center gap-3 rounded-[0.875rem] border-[1.5px] px-3.5 py-3 text-left transition-colors',
        selected
          ? 'border-sage-600 bg-sage-50 dark:border-sage-500 dark:bg-sage-950/30'
          : 'border-border bg-card hover:bg-muted/40',
      )}
    >
      <span
        className={`flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center rounded-[0.625rem] ${meta.iconBg}`}
      >
        <Icon className={`h-4 w-4 ${meta.iconColor}`} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-foreground">{pot.name}</p>
        <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
          {balanceLabel}
        </p>
      </div>
      <CaretRight className="h-[1.125rem] w-[1.125rem] shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}

function PotPickerList({ pots, selectedId, onSelect, mode }) {
  return (
    <div className="flex flex-col gap-2">
      {pots.map((pot) => {
        const accumulated = Number(pot.accumulated || 0);
        const remaining = Number(pot.remaining || 0);
        const balanceLabel =
          mode === 'from'
            ? pt.pots.move.savedBalance.replace('{amount}', formatEUR(accumulated))
            : pt.pots.move.remainingTarget
                .replace('{accumulated}', formatEUR(accumulated))
                .replace('{remaining}', formatEUR(remaining));
        return (
          <PotPickerCard
            key={pot.id}
            pot={pot}
            selected={selectedId === pot.id}
            onSelect={onSelect}
            balanceLabel={balanceLabel}
          />
        );
      })}
    </div>
  );
}

/**
 * @param {{ isOpen: boolean, onClose: () => void, pots: object[] }} props
 */
export function MovePotSheet({ isOpen, onClose, pots }) {
  const transfer = useTransferPotMutation();
  const activePots = useMemo(
    () => (pots || []).filter((pot) => !pot.is_closed),
    [pots],
  );

  const [fromPot, setFromPot] = useState(null);
  const [toPot, setToPot] = useState(null);
  const [amount, setAmount] = useState('');
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setFromPot(null);
      setToPot(null);
      setAmount('');
      setPicking(null);
      return;
    }
    if (activePots.length >= 2 && !fromPot) {
      setFromPot(activePots[0]);
      setToPot(activePots[1]);
    }
  }, [isOpen, activePots, fromPot]);

  const maxAmount = Number(fromPot?.accumulated || 0);
  const numericAmount = parsePtNumber(String(amount)) ?? 0;
  const canSubmit =
    fromPot &&
    toPot &&
    fromPot.id !== toPot.id &&
    numericAmount > 0 &&
    numericAmount <= maxAmount &&
    !transfer.isPending;

  const handleSubmit = () => {
    if (!canSubmit) {
      toast.error(pt.pots.move.invalidAmount);
      return;
    }
    transfer.mutate(
      {
        fromPotId: fromPot.id,
        toPotId: toPot.id,
        amount: numericAmount.toFixed(2),
      },
      {
        onSuccess: () => {
          toast.success(pt.pots.move.success);
          onClose();
        },
        onError: (err) => {
          const message =
            err?.amount ||
            err?.detail ||
            (typeof err === 'string' ? err : pt.pots.move.insufficientBalance);
          toast.error(message);
        },
      },
    );
  };

  const fromOptions = activePots.filter((pot) => pot.id !== toPot?.id);
  const toOptions = activePots.filter((pot) => pot.id !== fromPot?.id);

  return (
    <BottomSheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <BottomSheetContent title={pt.pots.move.title} footer={null}>
        <div className="flex flex-col gap-4 px-1 pb-2">
          {picking ? (
            <>
              <p className="text-sm font-semibold text-foreground">
                {picking === 'from' ? pt.pots.move.pickFrom : pt.pots.move.pickTo}
              </p>
              <PotPickerList
                pots={picking === 'from' ? fromOptions : toOptions}
                selectedId={picking === 'from' ? fromPot?.id : toPot?.id}
                mode={picking}
                onSelect={(pot) => {
                  if (picking === 'from') {
                    setFromPot(pot);
                    if (toPot?.id === pot.id) setToPot(null);
                  } else {
                    setToPot(pot);
                    if (fromPot?.id === pot.id) setFromPot(null);
                  }
                  setPicking(null);
                }}
              />
              <Button type="button" variant="ghost" onClick={() => setPicking(null)}>
                {pt.common.back}
              </Button>
            </>
          ) : (
            <>
              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">
                  {pt.pots.move.fromLabel}
                </p>
                {fromPot ? (
                  <PotPickerCard
                    pot={fromPot}
                    selected
                    onSelect={() => setPicking('from')}
                    balanceLabel={pt.pots.move.savedBalance.replace(
                      '{amount}',
                      formatEUR(fromPot.accumulated),
                    )}
                  />
                ) : (
                  <Button type="button" variant="outline" onClick={() => setPicking('from')}>
                    {pt.pots.move.pickFrom}
                  </Button>
                )}
              </div>

              <div className="flex justify-center">
                <span className="flex h-[2.125rem] w-[2.125rem] items-center justify-center rounded-full bg-sage-100 text-sage-700 dark:bg-sage-900/40 dark:text-sage-300">
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </span>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">
                  {pt.pots.move.toLabel}
                </p>
                {toPot ? (
                  <PotPickerCard
                    pot={toPot}
                    selected
                    onSelect={() => setPicking('to')}
                    balanceLabel={pt.pots.move.remainingTarget
                      .replace('{accumulated}', formatEUR(toPot.accumulated))
                      .replace('{remaining}', formatEUR(toPot.remaining))}
                  />
                ) : (
                  <Button type="button" variant="outline" onClick={() => setPicking('to')}>
                    {pt.pots.move.pickTo}
                  </Button>
                )}
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">
                  {pt.pots.move.amountLabel}
                </p>
                <div className="flex h-[3.875rem] items-center rounded-[0.875rem] border-[1.5px] border-border px-4">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                    placeholder="0"
                    className="w-full bg-transparent font-display text-[1.75rem] font-bold text-sage-700 outline-none dark:text-sage-300"
                    aria-label={pt.pots.move.amountLabel}
                  />
                  <span className="text-sm font-semibold text-muted-foreground">€</span>
                </div>
                <div className="mt-2.5 flex gap-2">
                  {QUICK_AMOUNTS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setAmount(String(chip))}
                      className={cn(
                        'flex-1 rounded-[0.6875rem] border-[1.5px] py-2 text-xs font-semibold transition-colors',
                        Number(amount) === chip
                          ? 'border-sage-600 bg-sage-50 text-sage-700 dark:border-sage-500 dark:bg-sage-950/30 dark:text-sage-300'
                          : 'border-border text-muted-foreground hover:bg-muted/40',
                      )}
                    >
                      €{chip}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setAmount(String(maxAmount || ''))}
                    className={cn(
                      'flex-1 rounded-[0.6875rem] border-[1.5px] py-2 text-xs font-semibold transition-colors',
                      Number(amount) === maxAmount && maxAmount > 0
                        ? 'border-sage-600 bg-sage-50 text-sage-700 dark:border-sage-500 dark:bg-sage-950/30 dark:text-sage-300'
                        : 'border-border text-muted-foreground hover:bg-muted/40',
                    )}
                  >
                    {pt.pots.move.chipAll}
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-2 text-[11.5px] leading-relaxed text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage-600" aria-hidden />
                <p>{pt.pots.move.note}</p>
              </div>

              <div className="flex gap-2 pt-1">
                <BottomSheetClose asChild>
                  <Button type="button" variant="ghost" className="flex-1">
                    {pt.common.cancel}
                  </Button>
                </BottomSheetClose>
                <Button
                  type="button"
                  variant="primary"
                  className="flex-1"
                  loading={transfer.isPending}
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {pt.pots.move.submit.replace(
                    '{amount}',
                    numericAmount > 0 ? formatEUR(numericAmount) : '€0',
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
